import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Location from './pages/Location';
import Contact from './pages/Contact';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import GoldPurchase from './pages/goldpurchase';
import BankBuyBack from './pages/bankbuyback';
import Goldloan from './pages/goldloan';
import InterestRates from './pages/interestrates';
import Payments from './pages/payments';
import OtherLoans from './pages/otherloans';
import Footer from './components/footer';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminDashboard from './pages/admin/dashboard';
import CustomerDashboard from './pages/customer/dashboard';
import EmployeeDashboard from './pages/employee/dashboard';
import CustomersPage from './pages/admin/customers';
import EmployeesPage from './pages/admin/employees';
import ReportsPage from './pages/admin/reports';
import LoansPage from './pages/admin/loans';
import ResetPassword from './pages/auth/ResetPassword';
import AdminAddLoan from './pages/admin/AddLoan';
import EmployeeAddLoan from './pages/employee/AddLoan';
import PendingRepayments from './pages/admin/pending-repayments';
import PaymentReminders from './pages/admin/payment-reminders';
import GoldReturns from './pages/admin/gold-returns';
import AuctionManagement from './pages/admin/auction-management';
import CronJobsManagement from './pages/admin/cron-jobs';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }: { children: React.ReactNode, allowedRoles?: string[] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Debug output
  // console.log('[ProtectedRoute]', {
  //   user,
  //   isAuthenticated,
  //   loading,
  //   allowedRoles
  // });

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles.length > 0 && (!user?.role || !allowedRoles.includes(user.role))) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Layouts for each role
const AdminLayout = () => (
  <>
    {/* Optionally add AdminSidebar here */}
    <Outlet />
  </>
);

const EmployeeLayout = () => (
  <>
    {/* Optionally add EmployeeSidebar here */}
    <Outlet />
  </>
);

const CustomerLayout = () => (
  <>
    <Outlet />
  </>
);

const AppRoutes: React.FC = () => {
  // Use the auth hook to handle token refresh
  useAuth();
  const location = useLocation();
  
  // Check if current route is admin, employee, or customer dashboard
  const isDashboardPage = location.pathname.startsWith('/admin/') || 
                         location.pathname.startsWith('/employee/') || 
                         location.pathname.startsWith('/customer/');

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="pt-20">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path='/gold-loan' element={<Goldloan/>}/>
          <Route path="/gold-purchase" element={<GoldPurchase />} />
          <Route path="/bank-buy-back" element={<BankBuyBack />} />
          <Route path="/other-loans" element={<OtherLoans/>}/>
          <Route path="/interest-rates" element={<InterestRates />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/location" element={<Location />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Admin routes */}
          <Route element={
              <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout />
              </ProtectedRoute>
          }>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/customers" element={<CustomersPage />} />
            <Route path="/admin/loans" element={<LoansPage />} />
            <Route path="/admin/employees" element={<EmployeesPage />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/add-loan" element={<AdminAddLoan />} />
            <Route path="/admin/pending-repayments" element={<PendingRepayments />} />
            <Route path="/admin/payment-reminders" element={<PaymentReminders />} />
            <Route path="/admin/gold-returns" element={<GoldReturns />} />
            <Route path="/admin/auction-management" element={<AuctionManagement />} />
            <Route path="/admin/cron-jobs" element={<CronJobsManagement />} />
          </Route>

          {/* Employee routes */}
          <Route element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeLayout />
              </ProtectedRoute>
          }>
            <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
            <Route path="/employee/customers" element={<CustomersPage />} />
            <Route path="/employee/loans" element={<LoansPage />} />
            <Route path="/employee/reports" element={<ReportsPage />} />
            <Route path="/employee/add-loan" element={<EmployeeAddLoan />} />
          </Route>

          {/* Customer routes */}
          <Route element={
              <ProtectedRoute allowedRoles={['customer']}>
              <CustomerLayout />
              </ProtectedRoute>
          }>
            <Route path="/customer/dashboard" element={<CustomerDashboard />} />
            {/* Add more customer routes here if needed */}
          </Route>
        </Routes>
      </main>
      {/* Only show footer on public pages, not on dashboard pages */}
      {!isDashboardPage && <Footer />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;